// MarketplacePage.jsx — PESACORE Marketplace
// Austère mais accessible · Même univers HomePageLab · Cas + Templates + Ressources · Créateurs certifiés

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Award, BookOpen, Filter, FlaskConical, Package, Search, ShieldCheck, Sparkles, Star, Users, Video, Zap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import api from '../config/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { ATELIERS_LIBRARY } from '../hooks/useAtelierUnlock';

// ── DATA ──────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all',       label: 'Tout',           count: 47 },
  { id: 'cas',       label: 'Cas d\'études',  count: 18 },
  { id: 'parcours',  label: 'Parcours',       count: 14 },
  { id: 'ressources',label: 'Ressources',     count: 15 },
];

const FILTERS = [
  { id: 'all',          label: 'Tous niveaux' },
  { id: 'debutant',     label: 'Débutant' },
  { id: 'intermédiaire',label: 'Intermédiaire' },
  { id: 'avancé',       label: 'Avancé' },
];

const DOMAINES = [
  'Tous domaines', 'Éducation', 'Entreprise', 'Insertion', 'Institution', 'Société', 'Management',
];

const computeBadge = (item) => {
  if (item.official) return 'OFFICIEL';
  if (item.author?.certified) return 'CERTIFIÉ';
  return null;
};

const computeBadgeColor = (item) => {
  const badge = computeBadge(item);
  if (badge === 'OFFICIEL') return 'amber';
  if (badge === 'CERTIFIÉ') return 'cyan';
  return null;
};

const mapAtelierToItem = (a) => ({
  id: a.id || a._id,
  type: 'atelier',
  official: a.official ?? false,
  title: a.nom || a.title || 'Atelier sans titre',
  subtitle: a.objectif || a.sousTitre || '',
  description: a.description || '',
  domaine: a.domaine || a.categorie || '',
  niveau: a.niveau || 'intermédiaire',
  
  steps: a.steps ?? null,
  metrics: a.metrics ?? [],
  price: a.price,
  currency: a.currency || '€',
  note: a.note,
  reviews: a.reviews,
  author: {
    name: a.author?.name || a.auteur || 'Anonyme',
    certified: a.author?.certified ?? false,
    official: a.author?.official ?? false,
  },
  badge: computeBadge(a),
  badgeColor: computeBadgeColor(a),
  tags: a.tags || [],
  featured: a.featured ?? false,
});

export function AuctionTimer({ endDate }) {
  const [timeLeft, setTimeLeft] = useState(calcTimeLeft(endDate));

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = calcTimeLeft(endDate);
      setTimeLeft(remaining);
      if (remaining.total <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [endDate]);

  function calcTimeLeft(end) {
    const diff = new Date(end).getTime() - Date.now();
    if (diff <= 0) return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      total: diff,
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  }

  if (timeLeft.total <= 0) {
    return <span className="text-red-400 text-[10px] font-sans uppercase tracking-wider">Enchère terminée</span>;
  }

  const pad = (n) => n.toString().padStart(2, '0');

  return (
    <div className="flex items-center gap-1 text-amber-400/80 text-[10px] font-mono">
      <span>{pad(timeLeft.days)}j</span>
      <span>{pad(timeLeft.hours)}h</span>
      <span>{pad(timeLeft.minutes)}m</span>
      <span>{pad(timeLeft.seconds)}s</span>
    </div>
  );
}

export function BidForm({ productId, currentBid, onBidPlaced }) {
  const [amount, setAmount] = useState(currentBid + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (amount <= currentBid) {
      setError('Votre enchère doit être supérieure à l\'enchère actuelle.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.post(`/products/${productId}/bid`, { amount });
      onBidPlaced?.(res.data.currentBid);
      setAmount(res.data.currentBid + 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="flex-1 relative">
        <input
          type="number"
          min={currentBid + 1}
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className="w-full bg-black/40 border border-white/50 text-white text-sm px-3 py-2 outline-none focus:border-amber-500/30"
          style={{ borderRadius: '2px' }}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-white/30 font-sans">€</span>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 bg-amber-500 text-black font-sans text-[10px] font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-50"
        style={{ borderRadius: '2px' }}
      >
        {loading ? '...' : 'Enchérir'}
      </button>
      {error && <p className="text-red-400 text-[9px] col-span-2">{error}</p>}
    </form>
  );
}

export function BidHistory({ bids = [] }) {
  if (!bids.length) return <p className="text-white/20 text-[10px] font-sans">Aucune enchère pour l'instant.</p>;

  return (
    <ul className="space-y-1 max-h-32 overflow-y-auto">
      {bids.slice().reverse().slice(0, 5).map((bid, i) => (
        <li key={i} className="flex justify-between text-[9px] font-mono text-white/40">
          <span>{bid.userId?.name || `Utilisateur ${bid.userId}`}</span>
          <span>{bid.amount} €</span>
        </li>
      ))}
    </ul>
  );
}

const ITEMS = [
  {
    id: 1, type: 'cas', official: true,
    title: 'Violence Symbolique', subtitle: 'Bourdieu · Reproduction sociale',
    description: 'Simulation causale immersive — 7 étapes, 4 trajectoires possibles. Mécanismes de reproduction scolaire, capital culturel, habitus.',
    domaine: 'Éducation', niveau: 'avancé', duree: '240 min', steps: 7,
    metrics: ['Capital Culturel', 'Champ Social', 'Habitus'],
    price: 290, currency: '€', note: 4.9, reviews: 34,
    author: { name: 'PESACORE', certified: true, official: true },
    badge: 'OFFICIEL', badgeColor: 'amber',
    tags: ['Bourdieu', 'Sociologie', 'Reproduction'],
    featured: true,
  },
  {
    id: 2, type: 'cas', official: true,
    title: 'Orientation & Identité', subtitle: 'Trouver sa voie',
    description: 'Diagnostic systémique des tensions de l\'orientation. 4 profils d\'acteurs, 5 métriques, contrefactuels intégrés.',
    domaine: 'Éducation', niveau: 'intermédiaire', duree: '25 min', steps: 5,
    metrics: ['Connaissance de soi', 'Posture active', 'Abnégation'],
    price: 190, currency: '€', note: 4.8, reviews: 21,
    author: { name: 'PESACORE', certified: true, official: true },
    badge: 'OFFICIEL', badgeColor: 'amber',
    tags: ['Orientation', 'Identité', 'Lycée'],
    featured: true,
  },
  {
    id: 3, type: 'cas', official: false,
    title: 'Conduite du Changement', subtitle: 'Restructuration organisationnelle',
    description: 'Simulation d\'impact d\'une restructuration sur les équipes. Graphe causal, bifurcations, traces décisionnelles exportables.',
    domaine: 'Management', niveau: 'avancé', duree: '180 min', steps: 6,
    metrics: ['Engagement', 'Résistance', 'Cohésion'],
    price: 340, currency: '€', note: 4.7, reviews: 12,
    author: { name: 'Cabinet Nexus RH', certified: true, official: false },
    badge: 'CERTIFIÉ', badgeColor: 'cyan',
    tags: ['RH', 'Management', 'Transformation'],
    featured: false,
  },
  {
    id: 4, type: 'parcours', official: true,
    title: 'Éducation & Société', subtitle: 'Parcours complet · 570 min',
    description: 'Parcours systémique sur les mécanismes éducatifs. Freire, Bourdieu, Bernstein — vécus, pas mémorisés.',
    domaine: 'Éducation', niveau: 'avancé', duree: '570 min', steps: 12,
    metrics: ['Compréhension systémique', 'Posture critique'],
    price: 490, currency: '€', note: 5.0, reviews: 8,
    author: { name: 'PESACORE', certified: true, official: true },
    badge: 'OFFICIEL', badgeColor: 'amber',
    tags: ['Éducation', 'Sociologie', 'Formation'],
    featured: true,
  },
  {
    id: 5, type: 'parcours', official: false,
    title: 'Insertion Professionnelle', subtitle: 'Du projet à l\'emploi',
    description: 'Parcours d\'accompagnement complet pour conseillers insertion. Outils de diagnostic, simulations d\'entretien, traçabilité Qualiopi.',
    domaine: 'Insertion', niveau: 'intermédiaire', duree: '320 min', steps: 8,
    metrics: ['Employabilité', 'Confiance', 'Réseau'],
    price: 380, currency: '€', note: 4.6, reviews: 17,
    author: { name: 'Institut Trajectoires', certified: true, official: false },
    badge: 'CERTIFIÉ', badgeColor: 'cyan',
    tags: ['Insertion', 'Emploi', 'Accompagnement'],
    featured: false,
  },
  {
    id: 6, type: 'ressources', official: true,
    title: 'Pack Qualiopi · Indicateurs', subtitle: 'Documentation complète',
    description: 'Ensemble de templates de relevés, indicateurs de suivi et documents réglementaires conformes Qualiopi 2026.',
    domaine: 'Institution', niveau: 'debutant', duree: null, steps: null,
    metrics: [],
    price: 120, currency: '€', note: 4.9, reviews: 43,
    author: { name: 'PESACORE', certified: true, official: true },
    badge: 'OFFICIEL', badgeColor: 'amber',
    tags: ['Qualiopi', 'Conformité', 'Documentation'],
    featured: false,
  },
  {
    id: 7, type: 'ressources', official: false,
    title: 'Bibliothèque Causes · Société', subtitle: '48 nœuds causaux documentés',
    description: 'Bibliothèque de relations causales documentées pour les cas sociologie, éducation et insertion. Sources académiques intégrées.',
    domaine: 'Société', niveau: 'avancé', duree: null, steps: null,
    metrics: [],
    price: 95, currency: '€', note: 4.5, reviews: 9,
    author: { name: 'Dr. M. Fontaine', certified: true, official: false },
    badge: 'CERTIFIÉ', badgeColor: 'cyan',
    tags: ['Causalité', 'Sociologie', 'Recherche'],
    featured: false,
  },
  {
    id: 8, type: 'cas', official: false,
    title: 'Postures Managériales', subtitle: 'Biais et autorité',
    description: 'Faire vivre les mécanismes de biais cognitifs et d\'autorité en situation. Destiné aux managers en développement.',
    domaine: 'Management', niveau: 'intermédiaire', duree: '120 min', steps: 4,
    metrics: ['Conscience des biais', 'Autorité', 'Équité'],
    price: 220, currency: '€', note: 4.4, reviews: 6,
    author: { name: 'Atelier du Leadership', certified: false, official: false },
    badge: null, badgeColor: null,
    tags: ['Management', 'Biais', 'Leadership'],
    featured: false,
  },
];

// Fallback statique enrichi avec les atelier locaux
const ITEMS_FALLBACK = [
  ...ITEMS, // l'ancien tableau statique (cas, parcours, ressources)
  ...ATELIERS_LIBRARY.map(mapAtelierToItem),
];

const STATS = [
  { val: '47',   label: 'Ressources disponibles', icon: Package },
  { val: '12',   label: 'Créateurs certifiés',    icon: ShieldCheck },
  { val: '340+', label: 'Organisations clientes', icon: Users },
  { val: '4.8',  label: 'Note moyenne',           icon: Star },
];

// ── LIVE TICKER ──────────────────────────────────────────────────────────────
const TICKER_EVENTS = [
  'Institut Nexus a acquis · Violence Symbolique',
  'Nouveau créateur certifié · Dr. M. Fontaine',
  'Cabinet RH Sigma a acquis · Conduite du Changement',
  'PESACORE a publié · Pack Qualiopi 2026',
  'École Nationale a acquis · Éducation & Société',
  '14 organisations actives ce mois',
];

function LiveTicker() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % TICKER_EVENTS.length), 3200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex items-center gap-3 overflow-hidden">
      <span className="flex items-center gap-1.5 shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[9px] font-sans uppercase tracking-widest text-emerald-400 opacity-70">Live</span>
      </span>
      <AnimatePresence mode="wait">
        <motion.span key={idx}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
          className="text-[10px] font-sans text-white/40 truncate">
          {TICKER_EVENTS[idx]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

function ItemCard({ item, onSelect }) {
  const typeIcon = {
    cas: FlaskConical,
    parcours: BookOpen,
    ressources: Package,
    atelier: Video,
  };
  const TypeIcon = typeIcon[item.type] || Package;

  const typeColors = {
    cas:        { border: 'border-purple-500/15 hover:border-purple-500/35', accent: 'text-purple-400', bg: 'bg-purple-500/5' },
    parcours:   { border: 'border-amber-500/15 hover:border-amber-500/35',  accent: 'text-amber-400',  bg: 'bg-amber-500/5'  },
    ressources: { border: 'border-cyan-500/15 hover:border-cyan-500/35',    accent: 'text-cyan-400',   bg: 'bg-cyan-500/5'   },
    atelier:    { border: 'border-emerald-500/15 hover:border-emerald-500/35', accent: 'text-emerald-400', bg: 'bg-emerald-500/5' },
  };
  const tc = typeColors[item.type] || {
    border: 'border-white/10 hover:border-white/20',
    accent: 'text-white/40',
    bg: 'bg-white/5',
  };

  const badgeColors = {
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/25',
    cyan:  'bg-cyan-500/10 text-cyan-400 border-cyan-500/25',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35 }}
      onClick={() => onSelect(item)}
      className={`group relative border ${tc.border} bg-black/40 backdrop-blur-sm cursor-pointer transition-all duration-300 flex flex-col overflow-hidden`}
      style={{ borderRadius: '2px' }}
    >
      <div className={`h-px w-full ${item.featured ? 'bg-gradient-to-r from-amber-500/60 via-amber-300/40 to-transparent' : 'bg-white/5'}`} />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className={`p-2 border border-white/5 ${tc.bg} shrink-0`}>
            <TypeIcon size={14} className={tc.accent} />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {item.badge && (
              <span className={`text-[8px] font-sans font-bold uppercase tracking-widest px-2 py-0.5 border ${badgeColors[item.badgeColor]}`}>
                {item.badge}
              </span>
            )}
            <span className="text-[8px] font-sans uppercase tracking-widest text-white/20 px-2 py-0.5 border border-white/5">
              {item.niveau}
            </span>
            {item.auction && (
              <span className="text-[8px] font-sans font-bold uppercase tracking-widest px-2 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/25">
                ENCHÈRE
              </span>
            )}
          </div>
        </div>

        <div className="mb-3">
          <h3 className="text-sm font-bold text-white leading-tight mb-0.5 group-hover:text-amber-300 transition-colors">
            {item.title}
          </h3>
          <p className="text-[10px] font-sans text-white/35 uppercase tracking-wider">{item.subtitle}</p>
        </div>

        <p className="text-[11px] text-white/50 leading-relaxed mb-4 flex-1 line-clamp-2">
          {item.description}
        </p>

        {item.metrics?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {item.metrics.slice(0, 3).map(m => (
              <span key={m} className="text-[8px] font-sans text-white/30 px-1.5 py-0.5 border border-white/5 bg-white/2">
                {m}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mb-4 text-[9px] font-sans text-white/25">
          {item.duree && <span>{item.duree}</span>}
          {item.steps && <span>·</span>}
          {item.steps && <span>{item.steps} étapes</span>}
          <span className="ml-auto flex items-center gap-1">
            <Star size={9} className="text-amber-400" fill="currentColor" />
            <span className="text-white/40">{item.note}</span>
            <span className="text-white/20">({item.reviews})</span>
          </span>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-white/5">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              {item.author?.certified && <ShieldCheck size={10} className="text-emerald-400" />}
              <span className="text-[9px] font-sans text-white/30">{item.author?.name || 'Anonyme'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-white">{item.price}{item.currency}</span>
            <ArrowRight size={12} className="text-white/20 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
          </div>
          {item.auction && item.auction.status === 'active' && (
            <div className="flex items-center gap-2 ml-auto">
              <AuctionTimer endDate={item.auction.endDate} />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}



// ── ITEM MODAL ────────────────────────────────────────────────────────────────
function ItemModal({ item, onClose, onUpdated }) {
  const { user } = useAuth();
  const [notified, setNotified] = useState(false);
  const [auctionForm, setAuctionForm] = useState({ startingPrice: 0, durationDays: 7 });
  const [auctionLoading, setAuctionLoading] = useState(false);

  const canManageAuction = ['superadmin', 'admin', 'collectivite', 'pro', 'entreprise']
    .includes(user?.role);

  const handleOpenAuction = async () => {
    setAuctionLoading(true);
    try {
      await api.patch(`/products/${item._id}/open-auction`, auctionForm);
      onUpdated?.(item._id);
    } catch (err) {
      console.error('Erreur ouverture enchère:', err);
    } finally {
      setAuctionLoading(false);
    }
  };

  const handleNotifyInterest = async (productId) => {
    setNotified(true);
    try {
      await api.post(`/products/${productId}/notify`);
    } catch (err) {
      console.warn('Notify failed silently', err);
    }
  };

  if (!item) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(2,6,23,0.92)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.25 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl border border-white/50 bg-[#0a0d14] overflow-hidden"
        style={{ borderRadius: '2px' }}
      >
        <div className="h-px bg-gradient-to-r from-amber-500/60 via-amber-300/30 to-transparent" />
        <div className="p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {item.badge && (
                  <span className="text-[8px] font-sans font-bold uppercase tracking-widest px-2 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/25">
                    {item.badge}
                  </span>
                )}
                <span className="text-[8px] font-sans uppercase tracking-widest text-white/20">
                  {item.type} · {item.domaine}
                </span>
              </div>
              <h2 className="text-2xl font-light text-white mb-1">{item.title}</h2>
              <p className="text-[10px] font-sans text-white/35 uppercase tracking-wider">{item.subtitle}</p>
            </div>
            <button onClick={onClose} className="text-white/20 hover:text-white/60 transition-colors text-xl">✕</button>
          </div>

          <p className="text-sm text-white/60 leading-relaxed mb-6 border-l border-amber-500/30 pl-4">
            {item.description}
          </p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Durée', val: item.durationMinutes || '—' },
              { label: 'Étapes', val: item.steps ? `${item.steps} étapes` : '—' },
              { label: 'Niveau', val: item.niveau },
            ].map((s, i) => (
              <div key={i} className="border border-white/5 bg-white/2 p-3">
                <div className="text-[8px] font-sans uppercase text-white/20 mb-1">{s.label}</div>
                <div className="text-sm font-bold text-white">{s.val}</div>
              </div>
            ))}
          </div>

          {item.metrics?.length > 0 && (
            <div className="mb-6">
              <div className="text-[9px] font-sans uppercase tracking-widest text-white/25 mb-2">Métriques simulées</div>
              <div className="flex flex-wrap gap-1.5">
                {item.metrics.map(m => (
                  <span key={m} className="text-[9px] font-sans text-white/50 px-2 py-1 border border-white/50 bg-white/3">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 mb-8">
            {item.tags?.map(t => (
              <span key={t} className="text-[8px] font-sans text-white/25 px-2 py-0.5 border border-white/5">
                #{t}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between pt-5 border-t border-white/5">
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                {item.author?.certified && <ShieldCheck size={11} className="text-emerald-400" />}
                <span className="text-[10px] font-sans text-white/40">{item.author?.name || 'Anonyme'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star size={10} className="text-amber-400" fill="currentColor" />
                <span className="text-[10px] font-sans text-white/30">{item.note} · {item.reviews} avis</span>
              </div>
            </div>
            {/* Section prix / enchère */}
{item.auction?.status === 'active' ? (
  <div className="border border-amber-500/15 bg-black/30 p-4 rounded w-full">
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="text-[8px] font-mono uppercase tracking-widest text-amber-400/60 mb-1">
          Enchère en cours
        </div>
        <span className="text-2xl font-bold text-white">
          {item.auction.currentBid}€
        </span>
        {item.auction.bids?.length > 0 && (
          <span className="text-[9px] text-white/30 ml-2">
            {item.auction.bids.length} offre{item.auction.bids.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <AuctionTimer endDate={item.auction.endDate} />
    </div>
    <BidHistory bids={item.auction.bids} />
    <div className="mt-3">
      <BidForm
        productId={item._id}
        currentBid={item.auction.currentBid}
        onBidPlaced={(newBid) => console.log('Nouvelle enchère :', newBid)}
      />
    </div>
  </div>

) : item.auction?.status === 'ended' ? (
  <div className="border border-white/5 p-4">
    <div className="text-[9px] font-mono uppercase text-white/20 mb-1">Enchère terminée</div>
    <span className="text-2xl font-bold text-white/40">
      {item.auction.currentBid}€
    </span>
  </div>

) : item.price > 0 ? (
  // Prix fixe défini
  <div className="flex items-center gap-4">
    <div>
      <span className="text-2xl font-bold text-white">{item.price}€</span>
      <span className="text-[9px] font-mono text-white/20 ml-2">accès permanent</span>
    </div>
    <button className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors">
      Acquérir <ArrowRight size={12} />
    </button>
  </div>

) : (
  // Pas de prix, pas d'enchère active — bouton d'intérêt
  <div className="w-full space-y-4">
    {/* Bouton utilisateur */}
    <div>
      <div className="text-[9px] font-mono text-white/25 mb-3 uppercase tracking-wider">
        Ce produit sera mis aux enchères prochainement
      </div>
      <button
        onClick={() => handleNotifyInterest(item._id)}
        disabled={notified}
        className={`w-full flex items-center justify-center gap-2 px-6 py-3 border font-mono text-[10px] font-bold uppercase tracking-widest transition-all ${
          notified
            ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5 cursor-default'
            : 'border-amber-500/30 text-amber-400 hover:bg-amber-500/5'
        }`}
      >
        {notified ? '✓ Vous serez notifié' : '⚡ Me notifier à l\'ouverture'}
      </button>
    </div>

    {/* Panneau admin — déclencher l'enchère */}
    {canManageAuction && (
      <div className="border border-white/8 bg-black/20 p-4 space-y-3">
        <div className="text-[8px] font-mono uppercase tracking-widest text-white/20">
          Ouvrir l'enchère
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[8px] font-mono text-white/20 uppercase block mb-1">
              Prix de départ (€)
            </label>
            <input
              type="number"
              min={0}
              value={auctionForm.startingPrice}
              onChange={e => setAuctionForm(f => ({ ...f, startingPrice: Number(e.target.value) }))}
              className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-1.5 outline-none focus:border-amber-500/30 font-mono"
              style={{ borderRadius: '2px' }}
            />
          </div>
          <div>
            <label className="text-[8px] font-mono text-white/20 uppercase block mb-1">
              Durée (jours)
            </label>
            <input
              type="number"
              min={1}
              max={90}
              value={auctionForm.durationDays}
              onChange={e => setAuctionForm(f => ({ ...f, durationDays: Number(e.target.value) }))}
              className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-1.5 outline-none focus:border-amber-500/30 font-mono"
              style={{ borderRadius: '2px' }}
            />
          </div>
        </div>
        <button
          onClick={handleOpenAuction}
          disabled={auctionLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-amber-500/15 transition-all disabled:opacity-50"
          style={{ borderRadius: '2px' }}
        >
          {auctionLoading ? '...' : '⚡ Ouvrir l\'enchère'}
        </button>
      </div>
    )}
  </div>
)}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}





// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function MarketplacePage() {
  const [category, setCategory]   = useState('all');
  const [niveau, setNiveau]       = useState('all');
  const [domaine, setDomaine]     = useState('Tous domaines');
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([
    { val: '—', label: 'Ressources disponibles', icon: Package },
    { val: '—', label: 'Créateurs', icon: ShieldCheck },
    { val: '340+', label: 'Organisations clientes', icon: Users }, // statique pour l'instant
    { val: '—', label: 'Note moyenne', icon: Star },
  ]);

  useEffect(() => {
    api.get('/products')
      .then(res => setItems(res.data))
      .catch(() => setItems(ITEMS_FALLBACK))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get('/products/stats')
      .then(res => {
        const { total, creators, avgNote } = res.data;
        setStats([
          { val: String(total), label: 'Ressources disponibles', icon: Package },
          { val: String(creators), label: 'Créateurs', icon: ShieldCheck },
          { val: '340+', label: 'Organisations clientes', icon: Users },
          { val: avgNote.toFixed(1), label: 'Note moyenne', icon: Star },
        ]);
      })
      .catch(() => { /* garder le fallback statique */ });
  }, []);

  const handleItemUpdated = (updatedId) => {
    api.get(`/products/${updatedId}`)
      .then(res => {
        setItems(prev => prev.map(i => 
          (i._id === updatedId || i.id === updatedId) ? { ...res.data, type: res.data.productType } : i
        ));
        setSelected(prev => prev?._id === updatedId ? { ...res.data, type: res.data.productType } : prev);
      })
      .catch(console.error);
  };

  const domaines = useMemo(() => {
    const raw = [...new Set(items.map((i) => i.domaine).filter(Boolean))].sort();
    return ['Tous domaines', ...raw];
  }, [items]);

  // Catégories avec compteurs dynamiques
  const categories = useMemo(() => {
    const counts = {
      all: items.length,
      cas: items.filter((i) => i.type === 'cas').length,
      parcours: items.filter((i) => i.type === 'parcours').length,
      atelier: items.filter((i) => i.type === 'atelier').length,
      ressources: 0, // réservé
    };
    return [
      { id: 'all', label: 'Tout', count: counts.all },
      { id: 'cas', label: 'Cas d\'études', count: counts.cas },
      { id: 'parcours', label: 'Parcours', count: counts.parcours },
      { id: 'atelier', label: 'Ateliers', count: counts.atelier },
      // { id: 'ressources', label: 'Ressources', count: counts.ressources },
    ];
  }, [items]);  

  // Filtrage
  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (category !== 'all' && item.type !== category) return false;
      if (niveau !== 'all' && item.niveau !== niveau) return false;
      if (domaine !== 'Tous domaines' && item.domaine !== domaine) return false;
      if (
        search &&
        !item.title.toLowerCase().includes(search.toLowerCase()) &&
        !item.description.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [items, category, niveau, domaine, search]);

  const featured = useMemo(() => items.filter((i) => i.featured), [items]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center text-white/30 font-sans text-sm">
        Chargement du catalogue…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200" style={{ fontFamily: "'Georgia', monospace" }}>

      {/* Grid background */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.025]" style={{
        backgroundImage: 'linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)',
        backgroundSize: '80px 80px',
      }} />

      {/* ── HERO ── */}
      <section className="relative pt-32 pb-16 px-6 border-b border-white/5">
        <div className="max-w-6xl mx-auto">

          {/* Live ticker */}
          <div className="flex items-center gap-6 mb-10 pb-4 border-b border-white/5">
            <LiveTicker />
            <div className="ml-auto hidden md:flex items-center gap-2 text-[9px] font-sans text-white/20 uppercase tracking-widest">
              <span className="w-1 h-1 rounded-full bg-white/15 inline-block" />
              PESACORE MARKETPLACE · v1.0
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
            <div>
              <motion.div
                initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 mb-5"
              >
                <span className="text-[8px] font-sans uppercase tracking-[0.4em] text-amber-400/60 px-2 py-1 border border-amber-500/15 bg-amber-500/5">
                  Architecture de connaissance
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-5xl md:text-6xl font-light text-white leading-none mb-4"
                style={{ letterSpacing: '-0.03em' }}
              >
                Acquérir.<br />
                <em className="not-italic font-black" style={{
                  background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                }}>Déployer.</em>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
                className="text-sm text-white/45 leading-relaxed max-w-md mb-8"
              >
                Cas d'études, parcours complets, ressources documentées — conçus pour le moteur PESA.
                Publiés par PESACORE et des créateurs certifiés.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                className="flex items-center gap-4"
              >
                <a href="/devenir-createur"
                  className="flex items-center gap-2 px-5 py-2.5 border border-white/10 text-white/50 text-[10px] font-sans uppercase tracking-widest hover:border-white/25 hover:text-white/80 transition-all">
                  <Award size={12} /> Devenir créateur certifié
                </a>
              </motion.div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              {stats.map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="border border-white/5 bg-black/30 p-5"
                >
                  <s.icon size={14} className="text-amber-400/60 mb-3" />
                  <div className="text-2xl font-black text-white mb-0.5">{s.val}</div>
                  <div className="text-[9px] font-sans uppercase tracking-widest text-white/25">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED ── */}
      <section className="py-12 px-6 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles size={12} className="text-amber-400" />
            <span className="text-[9px] font-sans uppercase tracking-[0.4em] text-amber-400/60">Sélection éditoriale</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map(item => (
              <ItemCard key={item.id} item={item} onSelect={setSelected} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CATALOGUE ── */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">

          {/* Search + filters bar */}
          <div className="flex flex-col md:flex-row gap-3 mb-6">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un cas, un domaine, un auteur…"
                className="w-full pl-9 pr-4 py-2.5 bg-black/40 border border-white/50 text-sm text-white/70 placeholder:text-white/20 outline-none focus:border-amber-500/30 transition-colors font-sans"
                style={{ borderRadius: '2px' }}
              />
            </div>
            <button
              onClick={() => setShowFilters(f => !f)}
              className={`flex items-center gap-2 px-4 py-2.5 border text-[10px] font-sans uppercase tracking-widest transition-all ${
                showFilters ? 'border-amber-500/40 text-amber-400 bg-amber-500/5' : 'border-white/50 text-white/30 hover:border-white/20'
              }`}
              style={{ borderRadius: '2px' }}
            >
              <Filter size={11} /> Filtres
            </button>
          </div>

          {/* Filters expanded */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mb-6"
              >
                <div className="flex flex-wrap gap-4 p-4 border border-white/5 bg-black/20">
                  {/* Niveau */}
                  <div>
                    <div className="text-[8px] font-sans uppercase tracking-widest text-white/60 mb-2">Niveau</div>
                    <div className="flex gap-1.5">
                      {FILTERS.map(f => (
                        <button key={f.id} onClick={() => setNiveau(f.id)}
                          className={`px-2.5 py-1 text-[9px] font-sans uppercase border transition-all ${
                            niveau === f.id
                              ? 'border-amber-500/40 text-amber-400 bg-amber-500/5'
                              : 'border-white/50 text-white/30 hover:border-white/20'
                          }`} style={{ borderRadius: '2px' }}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Domaine */}
                  <div>
                    <div className="text-[8px] font-sans uppercase tracking-widest text-white/20 mb-2">Domaine</div>
                    <div className="flex flex-wrap gap-1.5">
                      {domaines.map(d => (
                        <button key={d} onClick={() => setDomaine(d)}
                          className={`px-2.5 py-1 text-[9px] font-sans border transition-all ${
                            domaine === d
                              ? 'border-cyan-500/40 text-cyan-400 bg-cyan-500/5'
                              : 'border-white/50 text-white/30 hover:border-white/20'
                          }`} style={{ borderRadius: '2px' }}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Category tabs */}
          <div className="flex items-center gap-0 border-b border-white/5 mb-8">
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-3 text-[10px] font-sans uppercase tracking-widest transition-all border-b-2 -mb-px ${
                  category === cat.id
                    ? 'border-amber-400 text-amber-400'
                    : 'border-transparent text-white/30 hover:text-white/60'
                }`}>
                {cat.label}
                <span className={`text-[8px] px-1.5 py-0.5 ${
                  category === cat.id ? 'bg-amber-500/15 text-amber-400' : 'bg-white/5 text-white/20'
                }`}>
                  {cat.count}
                </span>
              </button>
            ))}
            <div className="ml-auto text-[9px] font-sans text-white/20">
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <AnimatePresence>
              {filtered.map(item => (
                <ItemCard key={item.id} item={item} onSelect={setSelected} />
              ))}
            </AnimatePresence>
            {filtered.length === 0 && (
              <div className="col-span-full py-20 text-center text-white/20 font-sans text-sm">
                Aucun résultat pour cette recherche.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── DEVENIR CRÉATEUR ── */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Award size={13} className="text-emerald-400" />
                <span className="text-[9px] font-sans uppercase tracking-[0.4em] text-emerald-400/60">Programme créateur</span>
              </div>
              <h2 className="text-3xl font-light text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
                Publiez votre expertise.<br />
                <span className="font-black text-emerald-400">Monétisez votre architecture.</span>
              </h2>
              <p className="text-sm text-white/40 leading-relaxed mb-6 max-w-md">
                Cabinets RH, chercheurs, formateurs — si vous maîtrisez le CaseCreator,
                votre travail a une valeur marchande sur la marketplace PESACORE.
              </p>
              <div className="space-y-2 mb-6">
                {[
                  'Certification par l\'équipe PESACORE',
                  'Commission sur chaque vente',
                  'Votre marque sur vos productions',
                  'Accès aux analytics de vente',
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] text-white/50">
                    <span className="w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
              <a href="/devenir-createur"
                className="inline-flex items-center gap-2 px-6 py-3 border border-emerald-500/30 text-emerald-400 text-[10px] font-sans uppercase tracking-widest hover:bg-emerald-500/5 transition-all">
                Déposer une candidature <ArrowRight size={12} />
              </a>
            </div>
            {/* Right — creator stats */}
            <div className="border border-white/5 bg-black/30 p-6">
              <div className="text-[9px] font-sans uppercase tracking-widest text-white/20 mb-4">Créateurs actifs</div>
              <div className="space-y-3">
                {[
                  { name: 'PESACORE',             type: 'Éditeur officiel',     items: 12, color: 'amber' },
                  { name: 'Cabinet Nexus RH',      type: 'Cabinet conseil',      items: 4,  color: 'cyan'  },
                  { name: 'Institut Trajectoires', type: 'OF Insertion',         items: 3,  color: 'emerald'},
                  { name: 'Dr. M. Fontaine',       type: 'Chercheur',            items: 1,  color: 'purple' },
                  { name: 'Atelier du Leadership', type: 'Cabinet management',   items: 1,  color: 'slate'  },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                    <div className={`w-1.5 h-1.5 rounded-full bg-${c.color}-400 shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-white/70 truncate">{c.name}</div>
                      <div className="text-[9px] font-sans text-white/25">{c.type}</div>
                    </div>
                    <div className="text-[10px] font-sans text-white/30">{c.items} pub.</div>
                    {c.name === 'PESACORE' && <ShieldCheck size={11} className="text-amber-400" />}
                    {c.name !== 'PESACORE' && <ShieldCheck size={11} className="text-emerald-400" />}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                <span className="text-[9px] font-sans text-white/20">Commission créateur</span>
                <span className="text-sm font-black text-emerald-400">70%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER CTA ── */}
      <section className="py-12 px-6 border-t border-white/5 bg-black/20">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-[9px] font-sans uppercase tracking-[0.4em] text-white/20 mb-1">PESACORE · Marketplace · v1.0</p>
            <p className="text-sm text-white/40">Chaque achat est un déploiement permanent dans votre organisation.</p>
          </div>
          <a href="/auth/register"
            className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-500 to-amber-400 text-black font-sans text-[10px] font-black uppercase tracking-widest hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all">
            Accéder à la marketplace <Zap size={12} />
          </a>
        </div>
      </section>

      {/* ── MODAL ── */}
      <AnimatePresence>
        {selected && (
          <ItemModal 
            item={selected} 
            onClose={() => setSelected(null)}
            onUpdated={handleItemUpdated}
          />
        )}
      </AnimatePresence>

    </div>
  );
}